import React, { useId } from "react";
import {
  Menu,
  Portal,
  Link,
  HStack,
  IconButton,
  Avatar,
  Tooltip,
} from "@commonsku/design-system";
import styled from "styled-components";
import {
  LuUser,
  LuLock,
  LuLayoutDashboard,
  LuSettings,
  LuLogOut,
} from "react-icons/lu";
import { useIdentity } from "../../hooks";

const ProfileAvatar = styled(Avatar)`
  &&& {
    --avatar-radius: 0.375rem;
  }
`;

const PROFILE_MENU_ITEMS = [
  {
    key: "profile",
    label: "Update Your Profile",
    Icon: LuUser,
    getHref: (identity: any) => `/user.php?id=${identity?.user_id}`,
  },
  {
    key: "security",
    label: "Password & Privacy",
    Icon: LuLock,
    getHref: () => "/security-two-factor.php",
  },
  {
    key: "old-dashboard",
    label: "Old Dashboard",
    Icon: LuLayoutDashboard,
    getHref: () => "/dashboard.php",
  },
  {
    key: "settings",
    label: "Settings",
    Icon: LuSettings,
    getHref: () => "/admin_settings_new.php",
  },
  {
    key: "logout",
    label: "Logout",
    Icon: LuLogOut,
    getHref: () => "/logout.php",
  },
];

function normalizeAvatarPath(path?: string) {
  if (!path) {
    return undefined;
  }
  if (path.startsWith("/") || path.startsWith("http")) {
    return path;
  }
  return "/" + path;
}

function ProfileMenu() {
  const triggerId = useId();
  const identity = useIdentity();
  const pic = normalizeAvatarPath(identity?.user_image_paths?.small);

  return (
    <Menu.Root
      ids={{ trigger: triggerId }}
      positioning={{ placement: "bottom-end", gutter: 8 }}
    >
      <Tooltip.Root
        ids={{ trigger: triggerId }}
        positioning={{ placement: "bottom-end", strategy: "fixed" }}
      >
        <Tooltip.Trigger asChild>
          <Menu.Trigger asChild>
            <IconButton
              label="Profile menu"
              variant={pic ? "ghost" : "outline"}
              colorPalette="neutral"
              size="sm"
              rounded="md"
              p="0"
            >
              {pic ? <ProfileAvatar size="sm" src={pic} /> : <LuUser size={18} />}
            </IconButton>
          </Menu.Trigger>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>Profile menu</Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>

      <Portal>
        <Menu.Positioner>
          <Menu.Content w="220px" p="8px">
            {PROFILE_MENU_ITEMS.map(({ key, label, Icon, getHref }) => (
              <Menu.Item key={key} value={key} p="6px" cursor="pointer" asChild>
                <Link
                  href={getHref(identity)}
                  display="block"
                  color="inherit"
                  _hover={{ textDecoration: "none", bg: "gray.100" }}
                >
                  <HStack gap="8px">
                    <Icon size={16} />
                    <span>{label}</span>
                  </HStack>
                </Link>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export default ProfileMenu;
